import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { API_URL } from '../../config/api';
import {
  formatEstimateLabel,
  estimateMinutesForSelect,
  DAILY_ESTIMATE_MAX_MINUTES,
} from '../../utils/estimateTime';
import { formatClockTime, normalizeClockTime } from './reminderUtils';
import { REMINDER_TEMPLATES, ReminderTemplateIcon } from './reminderTemplates';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: token ? `Bearer ${token}` : '' };
};

const COMMON_TIMEZONES = [
  'Asia/Kolkata',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

const DAYS_OF_WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const ReminderForm = ({ initialData, existingReminders = [], onSubmit, onCancel, saving }) => {
  const isEdit = Boolean(initialData?.id || initialData?._id);
  const currentUser = useSelector((state) => state.auth.user);
  const currentUserId = currentUser?.id;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [estimateTime, setEstimateTime] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [timezone, setTimezone] = useState(COMMON_TIMEZONES[0]);
  const [recurrenceType, setRecurrenceType] = useState('daily');
  const [time, setTime] = useState('09:00');
  const [dateForOnce, setDateForOnce] = useState(() => new Date().toISOString().slice(0, 10));
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [daysOfMonthInput, setDaysOfMonthInput] = useState('');
  const [showDaysOfMonthCalendar, setShowDaysOfMonthCalendar] = useState(false);
  const [createForAnother, setCreateForAnother] = useState(false);
  const [targetUserId, setTargetUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [tasks, setTasks] = useState([{ id: '', title: '' }]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setDescription(initialData.description || '');
      setEnabled(initialData.enabled !== false);
      setTimezone(initialData.timezone || COMMON_TIMEZONES[0]);
      const rec = initialData.recurrence || {};
      const recType = rec.type || 'daily';
      setRecurrenceType(recType);
      const mins = initialData.estimateTimeInMinutes;
      const minsNum = mins != null && mins !== '' ? Number(mins) : NaN;
      const estimateOk =
        !Number.isNaN(minsNum) &&
        minsNum > 0 &&
        !(recType === 'daily' && minsNum > DAILY_ESTIMATE_MAX_MINUTES);
      setEstimateTime(estimateOk ? String(minsNum) : '');
      setTime(rec.time || '09:00');
      setDateForOnce(rec.date || new Date().toISOString().slice(0, 10));
      setDaysOfWeek(Array.isArray(rec.daysOfWeek) ? [...rec.daysOfWeek] : []);
      setDaysOfMonthInput(
        Array.isArray(rec.daysOfMonth) && rec.daysOfMonth.length > 0
          ? rec.daysOfMonth.join(', ')
          : ''
      );
      const nextTasks = Array.isArray(initialData.tasks) && initialData.tasks.length
        ? initialData.tasks.map((task) => ({ id: task.id || '', title: task.title || '' }))
        : [{ id: '', title: '' }];
      setTasks(nextTasks);
    }
  }, [initialData]);

  const estimateSelectOptions = useMemo(
    () =>
      estimateMinutesForSelect(initialData?.estimateTimeInMinutes, {
        recurrenceType,
      }),
    [initialData?.estimateTimeInMinutes, recurrenceType]
  );

  const sameTimeReminders = useMemo(() => {
    const timeKey = normalizeClockTime(time);
    if (!timeKey) return [];
    const editingId = initialData?.id || initialData?._id;
    return (Array.isArray(existingReminders) ? existingReminders : []).filter((item) => {
      const itemId = item?.id || item?._id;
      if (editingId && itemId && String(itemId) === String(editingId)) return false;
      return normalizeClockTime(item?.recurrence?.time) === timeKey;
    });
  }, [existingReminders, time, initialData?.id, initialData?._id]);

  useEffect(() => {
    if (recurrenceType !== 'daily' || !estimateTime) return;
    const n = parseInt(estimateTime, 10);
    if (!Number.isNaN(n) && n > DAILY_ESTIMATE_MAX_MINUTES) {
      setEstimateTime('');
    }
  }, [recurrenceType, estimateTime]);

  useEffect(() => {
    if (!isEdit && createForAnother) {
      setUsersLoading(true);
      axios
        .get(`${API_URL}/users`, { headers: getAuthHeaders() })
        .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
        .catch(() => setUsers([]))
        .finally(() => setUsersLoading(false));
    }
  }, [isEdit, createForAnother]);

  const applyTemplate = (template) => {
    if (!template) return;
    setSelectedTemplateId(template.id);
    setTitle(template.title || '');
    setDescription(template.description || '');
    setRecurrenceType(template.recurrenceType || 'daily');
    setTime(template.time || '09:00');
    setDaysOfWeek(Array.isArray(template.daysOfWeek) ? [...template.daysOfWeek] : []);
    setDaysOfMonthInput(
      Array.isArray(template.daysOfMonth) && template.daysOfMonth.length
        ? template.daysOfMonth.join(', ')
        : ''
    );
    const nextTasks = Array.isArray(template.tasks) && template.tasks.length
      ? template.tasks.map((task) => ({ id: '', title: task }))
      : [{ id: '', title: '' }];
    setTasks(nextTasks);
    const mins = template.estimateMinutes;
    const recType = template.recurrenceType || 'daily';
    const estimateOk =
      mins != null &&
      mins > 0 &&
      !(recType === 'daily' && mins > DAILY_ESTIMATE_MAX_MINUTES);
    setEstimateTime(estimateOk ? String(mins) : '');
    setErrors({});
  };

  const toggleDayOfWeek = (day) => {
    setDaysOfWeek((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const daysOfMonthArray = daysOfMonthInput
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n) && n >= 1 && n <= 31);

  const toggleDayOfMonth = (day) => {
    const set = new Set(daysOfMonthArray);
    if (set.has(day)) {
      set.delete(day);
    } else {
      set.add(day);
    }
    setDaysOfMonthInput(Array.from(set).sort((a, b) => a - b).join(', '));
  };

  const validate = () => {
    const next = {};
    if (!title.trim()) next.title = 'Title is required';
    if (!timezone) next.timezone = 'Timezone is required';
    if (!time) next.time = 'Time is required';
    if (recurrenceType === 'weekly' && daysOfWeek.length === 0) {
      next.daysOfWeek = 'Select at least one day';
    }
    if (recurrenceType === 'monthly') {
      const parsed = daysOfMonthInput
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n) && n >= 1 && n <= 31);
      if (parsed.length === 0) {
        next.daysOfMonth = 'Enter at least one day (1–31)';
      }
    }
    if (recurrenceType === 'once') {
      if (!dateForOnce || !dateForOnce.trim()) next.dateForOnce = 'Date is required for once';
    }
    if (!isEdit && createForAnother && !targetUserId) {
      next.targetUserId = 'Select a user';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const recurrence = {
      type: recurrenceType,
      time,
    };
    if (recurrenceType === 'weekly') {
      recurrence.daysOfWeek = daysOfWeek;
    }
    if (recurrenceType === 'monthly') {
      recurrence.daysOfMonth = daysOfMonthInput
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n) && n >= 1 && n <= 31);
    }
    if (recurrenceType === 'once') {
      recurrence.date = dateForOnce;
    }

    const estimateTimeInMinutes = estimateTime ? parseInt(estimateTime, 10) : null;

    const payload = {
      title: title.trim(),
      description: description.trim() || undefined,
      estimateTimeInMinutes,
      enabled,
      timezone,
      recurrence,
      tasks: tasks
        .map((task) => ({
          id: task.id || undefined,
          title: (task.title || '').trim(),
        }))
        .filter((task) => task.title),
    };
    if (!isEdit && createForAnother && targetUserId) {
      payload.targetUserId = targetUserId;
    }

    onSubmit(payload);
  };

  const fieldLabelClass =
    'block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5 cursor-pointer';
  const inputClass =
    'w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500';

  const focusRepeats = () => {
    document.getElementById('reminder-repeats')?.focus();
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row min-h-0 gap-0">
        <div className="flex-1 min-w-0 space-y-4 sm:pr-5">
          {!isEdit && (
            <div>
              <span className={fieldLabelClass}>Templates</span>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                Slide and tap one to prefill.
              </p>
              <div className="flex gap-3 overflow-x-auto pb-1 -mx-0.5 px-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {REMINDER_TEMPLATES.map((template) => {
                  const selected = selectedTemplateId === template.id;
                  return (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => applyTemplate(template)}
                      className="shrink-0 w-[4.75rem] flex flex-col items-center gap-1.5"
                    >
                      <span
                        className={`w-12 h-12 rounded-full flex items-center justify-center border ${
                          selected
                            ? `${template.active} text-white border-transparent`
                            : `${template.bg} ${template.color} border-black/5 dark:border-white/10`
                        }`}
                      >
                        <ReminderTemplateIcon
                          id={template.id}
                          className="w-6 h-6"
                        />
                      </span>
                      <span
                        className={`text-[11px] leading-tight text-center font-semibold line-clamp-2 ${
                          selected ? template.color : 'text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {template.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <label htmlFor="reminder-title" className={fieldLabelClass}>
              Reminder title *
            </label>
            <input
              id="reminder-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={inputClass}
              placeholder="Reminder title"
            />
            {errors.title && (
              <p className="mt-0.5 text-xs text-red-500">{errors.title}</p>
            )}
          </div>

          <div>
            <label htmlFor="reminder-description" className={fieldLabelClass}>
              Description
            </label>
            <textarea
              id="reminder-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              className={`${inputClass} resize-y min-h-[7.5rem]`}
              placeholder="Optional details"
            />
          </div>

          <div>
            <label htmlFor="reminder-estimate" className={fieldLabelClass}>
              Daily estimated time
            </label>
            <select
              id="reminder-estimate"
              value={estimateTime}
              onChange={(e) => setEstimateTime(e.target.value)}
              className={inputClass}
            >
              <option value="">None (optional)</option>
              {estimateSelectOptions.map((m) => (
                <option key={m} value={String(m)}>
                  {formatEstimateLabel(m)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <span className={fieldLabelClass}>Tasks</span>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              Optional. Checked off each day this reminder fires.
            </p>
            <div className="space-y-2">
              {tasks.map((task, index) => (
                <div key={task.id || `new-${index}`} className="flex gap-2">
                  <input
                    type="text"
                    value={task.title}
                    onChange={(e) => {
                      const titleValue = e.target.value;
                      setTasks((prev) => prev.map((item, i) => (
                        i === index ? { ...item, title: titleValue } : item
                      )));
                    }}
                    className={inputClass}
                    placeholder={`Task ${index + 1}`}
                    maxLength={200}
                  />
                  {tasks.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setTasks((prev) => prev.filter((_, i) => i !== index))}
                      className="px-2 text-sm font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg"
                      aria-label="Remove task"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              {tasks.length < 20 && (
                <button
                  type="button"
                  onClick={() => setTasks((prev) => [...prev, { id: '', title: '' }])}
                  className="text-sm font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                >
                  + Add task
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="w-full sm:w-[20rem] lg:w-[22rem] flex-shrink-0 space-y-4 pt-4 sm:pt-0 sm:pl-5 border-t sm:border-t-0 sm:border-l border-gray-100 dark:border-gray-700">
          <div>
            <button
              type="button"
              onClick={focusRepeats}
              className={`${fieldLabelClass} text-left w-full`}
            >
              Schedule
            </button>
            <label htmlFor="reminder-repeats" className={`${fieldLabelClass} normal-case tracking-normal font-medium text-gray-600 dark:text-gray-300`}>
              Repeats
            </label>
            <select
              id="reminder-repeats"
              value={recurrenceType}
              onChange={(e) => {
                const next = e.target.value;
                setRecurrenceType(next);
                if (next === 'daily' && estimateTime) {
                  const n = parseInt(estimateTime, 10);
                  if (!Number.isNaN(n) && n > DAILY_ESTIMATE_MAX_MINUTES) {
                    setEstimateTime('');
                  }
                }
              }}
              className={inputClass}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="once">Once</option>
            </select>
          </div>

          {recurrenceType === 'once' && (
            <div>
              <label htmlFor="reminder-date" className={fieldLabelClass}>
                Date *
              </label>
              <input
                id="reminder-date"
                type="date"
                value={dateForOnce}
                onChange={(e) => setDateForOnce(e.target.value)}
                className={inputClass}
              />
              {errors.dateForOnce && (
                <p className="mt-0.5 text-xs text-red-500">{errors.dateForOnce}</p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="reminder-time" className={fieldLabelClass}>
              Time *
            </label>
            <input
              id="reminder-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className={inputClass}
            />
            {errors.time && (
              <p className="mt-0.5 text-xs text-red-500">{errors.time}</p>
            )}
          </div>

          {recurrenceType === 'weekly' && (
            <div>
              <span className={fieldLabelClass}>Days of week *</span>
              <div className="flex flex-wrap gap-1.5">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDayOfWeek(day)}
                    className={`px-2 py-1 text-xs rounded font-medium transition-colors ${
                      daysOfWeek.includes(day)
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
              {errors.daysOfWeek && (
                <p className="mt-0.5 text-xs text-red-500">{errors.daysOfWeek}</p>
              )}
            </div>
          )}

          {recurrenceType === 'monthly' && (
            <div className="relative">
              <label htmlFor="reminder-days-of-month" className={fieldLabelClass}>
                Days of month *
              </label>
              <div className="flex gap-2">
                <input
                  id="reminder-days-of-month"
                  type="text"
                  value={daysOfMonthInput}
                  onChange={(e) => setDaysOfMonthInput(e.target.value)}
                  placeholder="3, 7, 21"
                  className={`flex-1 ${inputClass}`}
                />
                <button
                  type="button"
                  onClick={() => setShowDaysOfMonthCalendar((v) => !v)}
                  className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 whitespace-nowrap"
                >
                  {showDaysOfMonthCalendar ? 'Hide calendar' : 'Open calendar'}
                </button>
              </div>
              {showDaysOfMonthCalendar && (
                <div className="mt-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Click to toggle day of month (1–31)</p>
                  <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDayOfMonth(day)}
                        className={`w-8 h-8 text-xs font-medium rounded transition-colors ${
                          daysOfMonthArray.includes(day)
                            ? 'bg-primary-500 text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {errors.daysOfMonth && (
                <p className="mt-0.5 text-xs text-red-500">{errors.daysOfMonth}</p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="reminder-timezone" className={fieldLabelClass}>
              Timezone *
            </label>
            <select
              id="reminder-timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className={inputClass}
            >
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>

          {isEdit && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="reminder-enabled"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="reminder-enabled" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                Enabled
              </label>
            </div>
          )}

          {isEdit && initialData?.targetUserId && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Reminder for:{' '}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {initialData.targetUsername || initialData.targetUserId || 'Another user'}
              </span>
            </p>
          )}

          {!isEdit && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="create-for-another"
                  checked={createForAnother}
                  onChange={(e) => {
                    setCreateForAnother(e.target.checked);
                    if (!e.target.checked) setTargetUserId('');
                  }}
                  className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="create-for-another" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  Create for another user (they must accept)
                </label>
              </div>
              {createForAnother && (
                <div>
                  <label htmlFor="reminder-target-user" className={fieldLabelClass}>
                    User
                  </label>
                  <select
                    id="reminder-target-user"
                    value={targetUserId}
                    onChange={(e) => setTargetUserId(e.target.value)}
                    className={inputClass}
                    disabled={usersLoading}
                  >
                    <option value="">Select user...</option>
                    {users
                      .filter((u) => u.id !== currentUserId)
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.username || u.firstName || u.lastName || u.id}
                        </option>
                      ))}
                  </select>
                  {errors.targetUserId && (
                    <p className="mt-0.5 text-xs text-red-500">{errors.targetUserId}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="sticky bottom-0 -mx-5 px-5 pt-3 pb-1 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
        {sameTimeReminders.length > 0 && (
          <div
            className="mb-3 overflow-hidden rounded-xl border border-amber-200/90 dark:border-amber-700/50 bg-gradient-to-r from-amber-50 via-orange-50 to-sky-50 dark:from-amber-950/50 dark:via-orange-950/30 dark:to-slate-900 shadow-sm"
            role="status"
          >
            <div className="h-0.5 w-full bg-gradient-to-r from-amber-400 via-orange-300 to-sky-400" />
            <div className="flex gap-3 px-3.5 py-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-sm">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Same time as {sameTimeReminders.length} existing {sameTimeReminders.length === 1 ? 'reminder' : 'reminders'}
                  </p>
                  <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/60 px-2 py-0.5 text-[11px] font-bold tracking-wide text-amber-800 dark:text-amber-200">
                    {formatClockTime(time) || time}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {sameTimeReminders.map((item) => (
                    <span
                      key={item.id || item._id || item.title}
                      className="inline-flex max-w-full items-center rounded-lg border border-white/80 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 px-2 py-1 text-xs font-medium text-gray-800 dark:text-gray-100 shadow-sm"
                    >
                      <span className="truncate">{item.title || 'Reminder'}</span>
                    </span>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                  They will open together in a single popup.
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-3 py-1.5 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? 'Saving...' : isEdit ? 'Update' : initialData ? 'Clone' : 'Create'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ReminderForm;
